import { ValueUnit } from '../types/Common';
import { ApplicationTreatment, SingleProductStats } from '../types/ApplicationTreatment';
import { createUnit } from '../i18n/unitQtyWrapper';
import { toIsoDateOnly } from './dateHelpers';
import { SeasonName } from '../types/Season';

type UnifiedApplicationTreatmentMeasure = {
  u: string;
  v: number;
};

type UnifiedApplicationTreatmentProduct = {
  id: string;
  name: string;
};

export type UnifiedApplicationTreatmentProductApplied = {
  rate: UnifiedApplicationTreatmentMeasure;
  amountApplied: UnifiedApplicationTreatmentMeasure;
  area: UnifiedApplicationTreatmentMeasure;
  growerAmountUnit: string;
  chemicalProduct: UnifiedApplicationTreatmentProduct;
};

export type UnifiedApplicationTreatmentEvent = {
  name: string;
  firstApplicationDateTime: string;
  lastApplicationDateTime: string;
  seasonName: SeasonName;
  seasonYear: number;
  area: UnifiedApplicationTreatmentMeasure;
  averageApplicationRate: UnifiedApplicationTreatmentMeasure;
  totalAmountApplied: UnifiedApplicationTreatmentMeasure;
  averageMachineSpeed: UnifiedApplicationTreatmentMeasure;
  chemicalProductsApplied: UnifiedApplicationTreatmentProductApplied[];
};

const normalizeUnitKey = (unit: string): string => unit.replace('km/h', 'kph');

const toValueUnit = (measure: UnifiedApplicationTreatmentMeasure): ValueUnit => ({
  v: measure.v,
  u: normalizeUnitKey(measure.u),
});

const convertQuantityUnit = (
  measure: ValueUnit,
  targetUnit: string | null | undefined,
): ValueUnit => {
  if (!targetUnit) {
    return measure;
  }

  try {
    const converted = createUnit(measure.v, measure.u).to(targetUnit);
    return {
      v: converted.scalar,
      u: normalizeUnitKey(converted.unitKey),
    };
  } catch (_error) {
    return measure;
  }
};

const convertRateUnit = (
  measure: ValueUnit,
  targetNumeratorUnit: string | null | undefined,
): ValueUnit => {
  if (!targetNumeratorUnit) {
    return measure;
  }

  const [, denominatorRaw] = measure.u.split('/');
  const denominator = denominatorRaw?.trim();
  if (!denominator) {
    return convertQuantityUnit(measure, targetNumeratorUnit);
  }

  const targetUnit = `${targetNumeratorUnit}/${denominator}`;
  return convertQuantityUnit(measure, targetUnit);
};

const mapProduct = (product: UnifiedApplicationTreatmentProductApplied): SingleProductStats => {
  const baseRate = toValueUnit(product.rate);
  const baseAmountApplied = toValueUnit(product.amountApplied);
  const growerSuppliedUnitRaw = product.growerAmountUnit?.trim() || '';
  const growerSuppliedUnit = growerSuppliedUnitRaw ? normalizeUnitKey(growerSuppliedUnitRaw) : null;
  const averageProductRate = convertRateUnit(baseRate, growerSuppliedUnit ?? undefined);
  const totalAmountApplied = convertQuantityUnit(baseAmountApplied, growerSuppliedUnit ?? undefined);
  const effectiveGrowerUnit = growerSuppliedUnit ?? baseAmountApplied.u;
  const productAppliedArea = toValueUnit(product.area);

  return {
    id: product.chemicalProduct.id,
    name: product.chemicalProduct.name,
    productAppliedDates: [],
    lastAppliedDate: '',
    averageProductRate,
    growerInputProductRateUnit: effectiveGrowerUnit,
    totalAmountApplied,
    relativeArea: productAppliedArea,
    productAppliedArea,
  };
};

const toApplicationTreatment = (
  event: UnifiedApplicationTreatmentEvent,
  seasonTag: string
): ApplicationTreatment | null => {
  const name = typeof event.name === 'string' ? event.name : '';
  const start = event.firstApplicationDateTime;
  const end = event.lastApplicationDateTime;
  const appliedArea = toValueUnit(event.area);
  const averageApplicationRate = toValueUnit(event.averageApplicationRate);
  const machineSpeed = toValueUnit(event.averageMachineSpeed);
  const products = event.chemicalProductsApplied.map(mapProduct);
  const localAppliedDate = toIsoDateOnly(end) ?? '';

  return {
    season: seasonTag,
    passName: name,
    startDateTime: start,
    endDateTime: end,
    localAppliedDate,
    appliedArea,
    averageApplicationRate,
    machineSpeed,
    machineId: '',
    productStats: products,
  };
};

export const adaptUnifiedEventsToTreatments = (
  events: UnifiedApplicationTreatmentEvent[] | undefined | null,
  seasonTag: string
): ApplicationTreatment[] => {
  if (!events?.length) {
    return [];
  }

  return events
    .map((event) => toApplicationTreatment(event, seasonTag))
    .filter((event): event is ApplicationTreatment => !!event);
};
